import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import CommandCenter from '@/pages/CommandCenter';
import PulseEntry from '@/pages/PulseEntry';
import HistoricalAnalysis from '@/pages/HistoricalAnalysis';
import ManagerView from '@/pages/ManagerView';
import TLView from '@/pages/TLView';
import SourceView from '@/pages/SourceView';
import AlertsPage from '@/pages/AlertsPage';
import MasterConfig from '@/pages/MasterConfig';
import VerificationPage from '@/pages/VerificationPage';
import SalesFunnel from '@/pages/SalesFunnel';
import CommitmentDashboard from '@/pages/CommitmentDashboard';
import MovementAnalytics from '@/pages/MovementAnalytics';
import PulseOperations from '@/pages/PulseOperations';
import ActivityLogs from '@/pages/ActivityLogs';
import NotificationDashboard from '@/pages/NotificationDashboard';
import RulesManagement from '@/pages/RulesManagement';
import HelpCenter from '@/pages/HelpCenter';
import ActiveGyms from '@/pages/ActiveGyms';
import GymPerformance from '@/pages/GymPerformance';
import RosterManagement from '@/pages/RosterManagement';
import MyRoster from '@/pages/MyRoster';
import AttendanceVerifications from '@/pages/AttendanceVerifications';
import AttendanceDashboard from '@/pages/AttendanceDashboard';
import AdminUserManagement from '@/pages/AdminUserManagement';
import SalesIntelligence from '@/pages/SalesIntelligence';
import InsideSalesIntelligence from '@/pages/InsideSalesIntelligence';
import AgentProfile from '@/pages/AgentProfile';
import ClosureExpected from '@/pages/ClosureExpected';
import Layout from '@/components/Layout';
import FMLayout from '@/pages/fm/FMLayout';
import FMHome from '@/pages/fm/FMHome';
import FMPortfolio from '@/pages/fm/FMPortfolio';
import FMKpiDashboard from '@/pages/fm/FMKpiDashboard';
import FMCompliance from '@/pages/fm/FMCompliance';
import FMSopChecklist from '@/pages/fm/FMSopChecklist';
import FMPipeline from '@/pages/fm/FMPipeline';
import FMRevenueTargets from '@/pages/fm/FMRevenueTargets';
import FMTrainerScorecard from '@/pages/fm/FMTrainerScorecard';
import FMCheckIn from '@/pages/fm/FMCheckIn';
import FMHuddle from '@/pages/fm/FMHuddle';
import FMEndOfDay from '@/pages/fm/FMEndOfDay';
import FMEscalations from '@/pages/fm/FMEscalations';
import SupplementTracker from '@/pages/SupplementTracker';
import DailyTargetDashboard from '@/pages/DailyTargetDashboard';
import Leaderboard from '@/pages/Leaderboard';
import TLPerformanceDetail from '@/pages/TLPerformanceDetail';
import { AttendanceGateProvider } from './context/AttendanceContext';
import CheckInModal from './components/attendance/CheckInModal';
import CheckOutModal from './components/attendance/CheckOutModal';
import ErrorBoundary from './components/ErrorBoundary';
import { initTelemetry, trackPageView, trackApiError, trackApiSuccess } from './services/telemetry';
import { useAutoUpdate } from './hooks/useAutoUpdate';
import './App.css';

// FM Coming Soon placeholder for routes not yet built
function ComingSoon({ title }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <h2 className="text-xl font-bold text-slate-800 mb-2">{title}</h2>
      <p className="text-sm text-slate-500 mb-6">This feature is coming soon.</p>
      <button
        onClick={() => window.history.back()}
        className="text-sm text-blue-500 underline"
      >
        Go back
      </button>
    </div>
  );
}

// Use relative URL to work with both HTTP and HTTPS
// Falls back to REACT_APP_BACKEND_URL if provided (for local dev)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export const axiosInstance = axios.create({
  baseURL: API,
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Stamp request start time for duration tracking in telemetry
  config._startTime = Date.now();
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    // v3: Track ALL successful API calls with timing
    trackApiSuccess(response);
    return response;
  },
  (error) => {
    // Track API errors via telemetry (skip telemetry endpoint itself to avoid loops)
    if (!error.config?.url?.includes('/telemetry/')) {
      trackApiError(error);
    }
    // Don't redirect on 401 from login endpoint - let the login page handle its own errors
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    // Only clear token & redirect if the request actually carried an auth token.
    // If no Authorization header was sent, this is a race condition (token not yet
    // set in localStorage when the request fired), NOT an expired session — so
    // clearing the token would destroy a valid session and force a needless logout.
    const hadAuthHeader = !!error.config?.headers?.Authorization;
    if (error.response?.status === 401 && !isLoginRequest && hadAuthHeader) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Initialize telemetry
initTelemetry();

function PrivateRoute({ children, allowedRoles }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Fitness managers should go to FM home, inside sales managers to inside sales, others to command center
    if (user.role === 'fitness_manager') {
      return <Navigate to="/fm" replace />;
    }
    if (user.role === 'inside_sales_manager') {
      return <Navigate to="/inside-sales" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

// Track route changes for telemetry
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  // Poll for new deployments every 2 min and auto-reload
  useAutoUpdate(120000);

  return (
    <ErrorBoundary>
    <div className="App">
      <AttendanceGateProvider>
        <BrowserRouter>
          <RouteTracker />
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* FM Operations - mobile-first layout, separate from desktop Layout */}
            <Route
              path="/fm/*"
              element={
                <PrivateRoute allowedRoles={['admin', 'fitness_manager', 'manager']}>
                  <FMLayout>
                    <Routes>
                      <Route path="/" element={<FMHome />} />
                      <Route path="/portfolio" element={<FMPortfolio />} />
                      <Route path="/kpi" element={<FMKpiDashboard />} />
                      <Route path="/sop" element={<FMSopChecklist />} />
                      <Route path="/compliance" element={<FMCompliance />} />
                      <Route path="/pipeline" element={<FMPipeline />} />
                      <Route path="/targets" element={<FMRevenueTargets />} />
                      <Route path="/trainers" element={<FMTrainerScorecard />} />
                      <Route path="/escalations" element={<FMEscalations />} />
                      <Route path="/checkin" element={<FMCheckIn />} />
                      <Route path="/huddle" element={<FMHuddle />} />
                      <Route path="/eod" element={<FMEndOfDay />} />
                    </Routes>
                  </FMLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <Layout>
                    <Routes>
                      <Route
                        path="/"
                        element={(() => {
                          const u = JSON.parse(localStorage.getItem('user') || '{}');
                          if (u.role === 'inside_sales_manager') return <Navigate to="/inside-sales" replace />;
                          if (u.role === 'fitness_manager') return <Navigate to="/fm" replace />;
                          return <Navigate to="/leaderboard" replace />;
                        })()}
                      />
                      <Route path="/command-center" element={<CommandCenter />} />
                      <Route path="/leaderboard" element={<Leaderboard />} />
                      <Route path="/leaderboard/:tlId" element={
                        <PrivateRoute allowedRoles={['admin', 'manager', 'inside_sales_manager']}>
                          <TLPerformanceDetail />
                        </PrivateRoute>
                      } />
                      <Route
                        path="/daily-targets"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader']}>
                            <DailyTargetDashboard />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/pulse-entry"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'team_leader']}>
                            <PulseEntry />
                          </PrivateRoute>
                        }
                      />
                      <Route path="/historical" element={<HistoricalAnalysis />} />
                      <Route
                        path="/sales-funnel"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader']}>
                            <SalesFunnel />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/sales-intelligence"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader']}>
                            <SalesIntelligence />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/inside-sales"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'inside_sales_manager', 'manager']}>
                            <InsideSalesIntelligence />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/inside-sales/:agentId"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'inside_sales_manager', 'manager']}>
                            <AgentProfile />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/closure-expected"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader']}>
                            <ClosureExpected />
                          </PrivateRoute>
                        }
                      />
                      <Route path="/commitment-dashboard" element={<CommitmentDashboard />} />
                      <Route path="/movement-analytics" element={<MovementAnalytics />} />
                      <Route path="/pulse-operations" element={<PulseOperations />} />
                      <Route path="/active-gyms" element={<ActiveGyms />} />
                      <Route path="/gym-performance" element={<GymPerformance />} />
                      <Route path="/manager-view" element={<ManagerView />} />
                      <Route path="/tl-view" element={<TLView />} />
                      <Route path="/source-view" element={<SourceView />} />
                      <Route path="/alerts" element={<AlertsPage />} />
                      <Route
                        path="/verification"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader']}>
                            <VerificationPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/master-config"
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <MasterConfig />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/activity-logs"
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <ActivityLogs />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/notifications"
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <NotificationDashboard />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/rules"
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <RulesManagement />
                          </PrivateRoute>
                        }
                      />
                      <Route path="/help" element={<HelpCenter />} />
                      <Route path="/help/:docId" element={<HelpCenter />} />
                      <Route
                        path="/roster-management"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader']}>
                            <RosterManagement />
                          </PrivateRoute>
                        }
                      />
                      <Route path="/my-roster" element={<MyRoster />} />
                      <Route
                        path="/attendance-verifications"
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <AttendanceVerifications />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/attendance-dashboard"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader', 'fitness_manager']}>
                            <AttendanceDashboard />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/user-management"
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <AdminUserManagement />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/supplements"
                        element={
                          <PrivateRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_team']}>
                            <SupplementTracker />
                          </PrivateRoute>
                        }
                      />
                    </Routes>
                  </Layout>
                </PrivateRoute>
              }
            />
          </Routes>
          {/* Add modals at root level (outside routes) */}
          <CheckInModal />
          <CheckOutModal />
        </BrowserRouter>
        <Toaster position="top-right" />
      </AttendanceGateProvider>
    </div>
    </ErrorBoundary>
  );
}

export default App;
